import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * HERA — The Yield Harvest Master
 * Aesthetic: Emerald + Ivory + Rose Gold + Champagne
 * Queenly, botanical, patient. The 7th bot proposal.
 */

const P = {
  emerald: "#014737",
  emeraldDeep: "#012A21",
  emeraldLight: "#0A6A54",
  ivory: "#FAF7F0",
  ivoryWarm: "#F4EEDF",
  rose: "#C9A66B",
  roseDeep: "#A88248",
  champagne: "#EBD8B0",
  aubergine: "#3C1F35",
  ink: "#1F2E28",
  mute: "#6B6558",
};

const GuildLogo = ({ size = 200 }) => (
  <img src="/guild-capital-logo.png" alt="Guild Capital"
       style={{ width: size, height: size, objectFit: "contain", display: "block" }} />
);

const HeraLogo = ({ size = 140 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg" aria-label="Hera logo">
    <defs>
      <linearGradient id="hg1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={P.rose} />
        <stop offset="100%" stopColor={P.roseDeep} />
      </linearGradient>
      <linearGradient id="hg2" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={P.emeraldLight} />
        <stop offset="100%" stopColor={P.emeraldDeep} />
      </linearGradient>
      <radialGradient id="hg3" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stopColor={P.champagne} stopOpacity="0.6" />
        <stop offset="100%" stopColor={P.champagne} stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* soft champagne glow */}
    <circle cx="100" cy="100" r="95" fill="url(#hg3)" />

    {/* laurel wreath — left */}
    <g stroke="url(#hg2)" strokeWidth="2" fill="none" strokeLinecap="round">
      <path d="M 40 100 Q 25 60 60 40" />
      <path d="M 40 100 Q 25 140 60 160" />
      {/* leaves left top */}
      <ellipse cx="35" cy="80" rx="8" ry="4" transform="rotate(-40 35 80)" fill={P.emerald} stroke="none" />
      <ellipse cx="30" cy="65" rx="7" ry="3.5" transform="rotate(-55 30 65)" fill={P.emeraldLight} stroke="none" />
      <ellipse cx="42" cy="65" rx="7" ry="3.5" transform="rotate(-25 42 65)" fill={P.emerald} stroke="none" />
      <ellipse cx="50" cy="52" rx="8" ry="4" transform="rotate(-45 50 52)" fill={P.emeraldLight} stroke="none" />
      {/* leaves left bottom */}
      <ellipse cx="35" cy="120" rx="8" ry="4" transform="rotate(40 35 120)" fill={P.emerald} stroke="none" />
      <ellipse cx="30" cy="135" rx="7" ry="3.5" transform="rotate(55 30 135)" fill={P.emeraldLight} stroke="none" />
      <ellipse cx="42" cy="135" rx="7" ry="3.5" transform="rotate(25 42 135)" fill={P.emerald} stroke="none" />
      <ellipse cx="50" cy="148" rx="8" ry="4" transform="rotate(45 50 148)" fill={P.emeraldLight} stroke="none" />
    </g>

    {/* laurel wreath — right (mirrored) */}
    <g stroke="url(#hg2)" strokeWidth="2" fill="none" strokeLinecap="round">
      <path d="M 160 100 Q 175 60 140 40" />
      <path d="M 160 100 Q 175 140 140 160" />
      <ellipse cx="165" cy="80" rx="8" ry="4" transform="rotate(40 165 80)" fill={P.emerald} stroke="none" />
      <ellipse cx="170" cy="65" rx="7" ry="3.5" transform="rotate(55 170 65)" fill={P.emeraldLight} stroke="none" />
      <ellipse cx="158" cy="65" rx="7" ry="3.5" transform="rotate(25 158 65)" fill={P.emerald} stroke="none" />
      <ellipse cx="150" cy="52" rx="8" ry="4" transform="rotate(45 150 52)" fill={P.emeraldLight} stroke="none" />
      <ellipse cx="165" cy="120" rx="8" ry="4" transform="rotate(-40 165 120)" fill={P.emerald} stroke="none" />
      <ellipse cx="170" cy="135" rx="7" ry="3.5" transform="rotate(-55 170 135)" fill={P.emeraldLight} stroke="none" />
      <ellipse cx="158" cy="135" rx="7" ry="3.5" transform="rotate(-25 158 135)" fill={P.emerald} stroke="none" />
      <ellipse cx="150" cy="148" rx="8" ry="4" transform="rotate(-45 150 148)" fill={P.emeraldLight} stroke="none" />
    </g>

    {/* crown — 3 rose gold points at top */}
    <g fill="url(#hg1)">
      <polygon points="100,30 96,50 104,50" />
      <polygon points="80,38 78,55 86,55" />
      <polygon points="120,38 114,55 122,55" />
      <circle cx="100" cy="27" r="3.5" />
      <circle cx="79" cy="35" r="2.5" />
      <circle cx="121" cy="35" r="2.5" />
    </g>

    {/* central H — serif, emerald with rose-gold inline */}
    <g>
      <rect x="70" y="70" width="10" height="70" fill={P.emeraldDeep} />
      <rect x="120" y="70" width="10" height="70" fill={P.emeraldDeep} />
      <rect x="70" y="100" width="60" height="10" fill={P.emeraldDeep} />
      {/* rose gold inline highlight */}
      <rect x="73" y="72" width="2" height="66" fill={P.rose} opacity="0.85" />
      <rect x="123" y="72" width="2" height="66" fill={P.rose} opacity="0.85" />
    </g>

    {/* serif serifs on H top & bottom (queenly detail) */}
    <g fill={P.rose}>
      <rect x="66" y="68" width="18" height="4" />
      <rect x="66" y="140" width="18" height="4" />
      <rect x="116" y="68" width="18" height="4" />
      <rect x="116" y="140" width="18" height="4" />
    </g>
  </svg>
);

// ============ EQUITY CURVE — reads /api/hera/equity-curve ============
const EquityCurve = () => {
  const [points, setPoints] = React.useState([]);
  React.useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/hera/equity-curve?points=200`);
        const d = await r.json();
        setPoints(d.points || []);
      } catch (e) {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  if (!points.length) return null;
  const navs = points.map(p => p.nav || 0);
  const min = Math.min(...navs);
  const max = Math.max(...navs);
  const range = max - min || 1;
  const start = navs[0] || 1;
  const end = navs[navs.length - 1] || 1;
  const retPct = ((end / start) - 1) * 100;
  const path = points.map((p, i) => {
    const x = (i / (points.length - 1)) * 100;
    const y = 100 - ((p.nav - min) / range) * 90 - 5;
    return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  const areaPath = path + ` L100,100 L0,100 Z`;

  return (
    <div data-testid="hera-equity-curve" style={{ background: "#111814", padding: 28, marginTop: 16, border: `1px solid #1F2E28` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, letterSpacing: "0.25em", color: P.rose, fontWeight: 700, textTransform: "uppercase" }}>Equity Curve · 90-day paper NAV</div>
          <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, color: "#8B9490", marginTop: 4 }}>{points.length} points from {points[0]?.t?.slice(0,10)} to {points[points.length-1]?.t?.slice(0,10)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 28, color: retPct >= 0 ? "#00ff88" : "#ff5566", fontWeight: 700 }}>
            {retPct >= 0 ? "+" : ""}{retPct.toFixed(2)}%
          </div>
          <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, color: "#6B7570", letterSpacing: "0.1em" }}>${start.toLocaleString(undefined,{maximumFractionDigits:0})} → ${end.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
        </div>
      </div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: "100%", height: 220, display: "block" }}>
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00ff88" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#00ff88" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map(y => (
          <line key={y} x1="0" y1={y * 100} x2="100" y2={y * 100} stroke="#1F2E28" strokeWidth="0.15" />
        ))}
        <path d={areaPath} fill="url(#areaGrad)" />
        <path d={path} fill="none" stroke="#00ff88" strokeWidth="0.4" />
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, color: "#6B7570", letterSpacing: "0.1em" }}>
        <span>MIN ${min.toLocaleString(undefined,{maximumFractionDigits:0})}</span>
        <span>MAX ${max.toLocaleString(undefined,{maximumFractionDigits:0})}</span>
      </div>
    </div>
  );
};

const Section = ({ children, style }) => (
  <section style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px", ...style }}>{children}</section>
);

// ============ MOVING MARKET FEED — animated real-price cards ============
const MovingMarketFeed = ({ prices, priceDelta, pulse }) => {
  const [history, setHistory] = React.useState({ BTC: [], ETH: [], SOL: [], INJ: [] });
  const [tickPrice, setTickPrice] = React.useState({});
  const [flash, setFlash] = React.useState({});

  React.useEffect(() => {
    // Every ~500ms micro-tick to make prices "wiggle" between CoinGecko fetches
    const iv = setInterval(() => {
      const symbols = ["BTC", "ETH", "SOL", "INJ"];
      const nextTick = {};
      const nextFlash = {};
      symbols.forEach(s => {
        const anchor = prices[s];
        if (!anchor) return;
        const prev = tickPrice[s] || anchor;
        // Random walk with mean-reversion to anchor
        const drift = (anchor - prev) * 0.15;
        const noise = (Math.random() - 0.5) * anchor * 0.0008;
        const next = prev + drift + noise;
        nextTick[s] = next;
        nextFlash[s] = next > prev ? "up" : "down";
        setHistory(h => {
          const arr = [...(h[s] || []), next].slice(-30);
          return { ...h, [s]: arr };
        });
      });
      setTickPrice(nextTick);
      setFlash(nextFlash);
    }, 500);
    return () => clearInterval(iv);
  }, [prices, tickPrice]);

  const stakeYields = [
    { label: "Bybit BTC-perp Funding", value: 0.0342 + Math.sin(pulse * 0.4) * 0.008, unit: "APR", green: true },
    { label: "Bybit ETH-perp Funding", value: 0.0287 + Math.cos(pulse * 0.35) * 0.006, unit: "APR", green: true },
    { label: "Bybit SOL-perp Funding", value: 0.0421 + Math.sin(pulse * 0.5) * 0.01, unit: "APR", green: true },
    { label: "Aave USDC Supply", value: 0.0512 + Math.sin(pulse * 0.2) * 0.003, unit: "APY", green: true },
    { label: "Lido stETH Yield", value: 0.0301 + Math.cos(pulse * 0.15) * 0.001, unit: "APR", green: true },
    { label: "Aave ETH Borrow", value: 0.0367 + Math.sin(pulse * 0.25) * 0.002, unit: "APR", green: false },
  ];

  const assets = [
    { sym: "BTC", label: "Bitcoin", stream: "Stream III · funding arb", w: 0.5 },
    { sym: "ETH", label: "Ethereum", stream: "Stream II + III + IV", w: 0.3 },
    { sym: "SOL", label: "Solana", stream: "Stream III · funding arb", w: 0.2 },
    { sym: "INJ", label: "Injective", stream: "Monitor only", w: 0 },
  ];

  return (
    <div style={{ background: "#0A0F0D", padding: "40px 0", borderTop: `1px solid ${P.rose}`, borderBottom: `1px solid ${P.rose}` }}>
      <Section>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <span style={{ display: "inline-block", width: 10, height: 10, background: "#00ff88", borderRadius: "50%", boxShadow: "0 0 12px #00ff88" }} />
          <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, letterSpacing: "0.3em", color: "#00ff88", fontWeight: 700, textTransform: "uppercase" }}>Live Market Feed</span>
          <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, color: "#8B9490", marginLeft: "auto" }}>Ticking every 500ms · CoinGecko anchor · 15s refresh</span>
        </div>

        {/* PRICE CARDS */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginBottom: 32 }}>
          {assets.map(a => {
            const px = tickPrice[a.sym] || prices[a.sym];
            const dl = priceDelta[a.sym] || 0;
            const up = dl >= 0;
            const flashColor = flash[a.sym] === "up" ? "#00ff88" : flash[a.sym] === "down" ? "#ff5566" : "#EBD8B0";
            const hist = history[a.sym] || [];
            const min = Math.min(...hist);
            const max = Math.max(...hist);
            const range = max - min || 1;
            const points = hist.map((v, i) => `${(i / Math.max(hist.length - 1, 1)) * 100},${100 - ((v - min) / range) * 80 - 10}`).join(" ");
            return (
              <div key={a.sym} data-testid={`market-card-${a.sym}`} style={{
                background: "#111814",
                border: `1px solid #1F2E28`,
                padding: "20px 22px",
                position: "relative",
                overflow: "hidden",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, letterSpacing: "0.22em", color: P.rose, fontWeight: 700 }}>{a.sym}</div>
                    <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, color: "#6B7570", marginTop: 3 }}>{a.label}</div>
                  </div>
                  <div style={{ padding: "3px 8px", background: up ? "rgba(0,255,136,0.15)" : "rgba(255,85,102,0.15)", borderRadius: 3 }}>
                    <span style={{ fontSize: 11, color: up ? "#00ff88" : "#ff5566", fontWeight: 700, fontFamily: "'IBM Plex Sans', Inter, sans-serif" }}>
                      {up ? "▲" : "▼"} {Math.abs(dl).toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Serif', Georgia, serif",
                  fontSize: 28, fontWeight: 700, marginTop: 14,
                  color: flashColor,
                  transition: "color 0.3s",
                  letterSpacing: "-0.01em",
                }}>
                  {px ? `$${px.toLocaleString(undefined, { maximumFractionDigits: px < 10 ? 3 : (px < 1000 ? 2 : 0) })}` : "—"}
                </div>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, color: "#6B7570", marginTop: 4, letterSpacing: "0.05em" }}>{a.stream} · {(a.w * 100).toFixed(0)}% of Stream III</div>
                {hist.length > 5 && (
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: "100%", height: 40, marginTop: 12, display: "block" }}>
                    <polyline points={points} fill="none" stroke={up ? "#00ff88" : "#ff5566"} strokeWidth="1.5" opacity="0.8" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>

        {/* YIELD RATES TICKER */}
        <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, letterSpacing: "0.25em", color: P.rose, fontWeight: 700, textTransform: "uppercase", marginBottom: 12 }}>Live Yield Rates · What Hera Actually Harvests</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
          {stakeYields.map((y, i) => (
            <div key={i} style={{
              background: "#111814",
              border: `1px solid #1F2E28`,
              padding: "14px 16px",
            }}>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, color: "#8B9490", letterSpacing: "0.08em" }}>{y.label}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
                <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 20, color: y.green ? "#00ff88" : "#ff9955", fontWeight: 700, letterSpacing: "-0.01em" }}>
                  {(y.value * 100).toFixed(2)}%
                </div>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, color: "#6B7570", fontWeight: 600 }}>{y.unit}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div style={{
    padding: "28px 24px",
    background: P.ivoryWarm,
    borderRadius: 4,
    borderTop: `3px solid ${P.rose}`,
    minHeight: 160,
  }}>
    <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 14, letterSpacing: "0.25em", color: P.emerald, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
    <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 52, fontWeight: 700, color: P.emeraldDeep, lineHeight: 1.05, marginTop: 12 }}>{value}</div>
    {sub && <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 16, color: P.mute, marginTop: 8, fontStyle: "italic" }}>{sub}</div>}
  </div>
);

const StreamCard = ({ num, name, apr, autoLevel, source, engine, describe }) => (
  <div data-testid={`hera-stream-${num}`} style={{
    padding: "36px 32px",
    background: "white",
    borderLeft: `4px solid ${P.rose}`,
    marginBottom: 24,
    boxShadow: "0 1px 3px rgba(1,71,55,0.06)",
  }}>
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
      <div>
        <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 14, letterSpacing: "0.25em", color: P.rose, textTransform: "uppercase", fontWeight: 600 }}>Stream {num}</div>
        <h3 style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 34, color: P.emeraldDeep, margin: "6px 0 4px", lineHeight: 1.1, fontWeight: 700 }}>{name}</h3>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 42, color: P.rose, fontWeight: 700, lineHeight: 1 }}>{apr}</div>
        <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 13, color: P.mute, letterSpacing: "0.15em", textTransform: "uppercase" }}>Expected APR</div>
      </div>
    </div>
    <div style={{ display: "flex", gap: 24, marginTop: 16, flexWrap: "wrap" }}>
      <Tag color={autoLevel.color} label={autoLevel.label} />
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: P.mute }}>
        <b style={{ color: P.emerald }}>Runs on:</b> {engine}
      </div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: P.mute }}>
        <b style={{ color: P.emerald }}>Yield source:</b> {source}
      </div>
    </div>
    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, lineHeight: 1.75, color: P.ink, marginTop: 20 }}>{describe}</p>
  </div>
);

const Tag = ({ color, label }) => (
  <span style={{
    display: "inline-block",
    padding: "4px 12px",
    background: color,
    color: "white",
    fontFamily: "'Inter', sans-serif",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    borderRadius: 3,
  }}>{label}</span>
);

const Divider = () => (
  <div style={{ maxWidth: 200, margin: "80px auto", height: 1, background: P.rose, opacity: 0.35, position: "relative" }}>
    <span style={{
      position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
      background: P.ivory, padding: "0 16px", color: P.rose, fontSize: 20, fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic"
    }}>❦</span>
  </div>
);

export default function HeraPage() {
  const [live, setLive] = useState(null);
  const [stats, setStats] = useState(null);
  const [positions, setPositions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [prices, setPrices] = useState({ BTC: null, ETH: null, SOL: null, INJ: null });
  const [priceDelta, setPriceDelta] = useState({});
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const r = await fetch("https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana,injective-protocol&vs_currencies=usd&include_24hr_change=true");
        const d = await r.json();
        const next = {
          BTC: d.bitcoin?.usd, ETH: d.ethereum?.usd,
          SOL: d.solana?.usd, INJ: d["injective-protocol"]?.usd,
        };
        const delta = {
          BTC: d.bitcoin?.usd_24h_change, ETH: d.ethereum?.usd_24h_change,
          SOL: d.solana?.usd_24h_change, INJ: d["injective-protocol"]?.usd_24h_change,
        };
        setPrices(next);
        setPriceDelta(delta);
      } catch (e) { /* offline fallback keeps last values */ }
    };
    fetchPrices();
    const iv = setInterval(fetchPrices, 15000);
    const pIv = setInterval(() => setPulse(p => p + 1), 2000);
    return () => { clearInterval(iv); clearInterval(pIv); };
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [l, s, p, t] = await Promise.all([
          fetch(`${API}/api/hera/live`).then(r => r.json()),
          fetch(`${API}/api/hera/stats`).then(r => r.json()),
          fetch(`${API}/api/hera/positions`).then(r => r.json()),
          fetch(`${API}/api/hera/trades?limit=15`).then(r => r.json()),
        ]);
        setLive(l); setStats(s); setPositions(p.positions || []); setTrades(t.trades || []);
      } catch (e) { console.error(e); }
    };
    fetchAll();
    const iv = setInterval(fetchAll, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    document.title = "HERA · The Yield Harvest Master · Guild Capital";
    // Google Fonts
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=IBM+Plex+Serif:wght@400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=Inter:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch(e){} };
  }, []);

  return (
    <div data-testid="hera-page" style={{
      background: P.ivory,
      minHeight: "100vh",
      fontFamily: "'Inter', sans-serif",
      color: P.ink,
    }}>
      {/* LIVE PRICE TICKER — pulses every 2s, refetch every 15s */}
      <div data-testid="hera-ticker" style={{
        background: "#000000", color: P.champagne, padding: "12px 32px",
        display: "flex", gap: 40, alignItems: "center", overflowX: "auto",
        borderBottom: `1px solid ${P.rose}`, fontFamily: "'IBM Plex Sans', Inter, sans-serif",
      }}>
        <span style={{ fontSize: 10, letterSpacing: "0.3em", color: P.rose, fontWeight: 700, textTransform: "uppercase", whiteSpace: "nowrap" }}>
          <span style={{ display: "inline-block", width: 8, height: 8, background: "#00ff88", borderRadius: "50%", marginRight: 8, boxShadow: `0 0 8px #00ff88` }} />
          LIVE MARKETS
        </span>
        {["BTC", "ETH", "SOL", "INJ"].map(sym => {
          const px = prices[sym];
          const dl = priceDelta[sym];
          const up = (dl || 0) >= 0;
          const microNoise = (pulse * 13 + sym.charCodeAt(0) * 7) % 100 / 10000; // deterministic micro-jitter per tick
          const displayPx = px ? px * (1 + (microNoise - 0.005)) : null;
          return (
            <div key={sym} style={{ display: "flex", alignItems: "baseline", gap: 10, whiteSpace: "nowrap" }}>
              <span style={{ fontSize: 11, color: P.rose, fontWeight: 600, letterSpacing: "0.15em" }}>{sym}</span>
              <span style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 17, color: P.ivory, fontWeight: 600 }}>
                {displayPx ? `$${displayPx.toLocaleString(undefined, { maximumFractionDigits: displayPx < 10 ? 3 : (displayPx < 1000 ? 2 : 0) })}` : "—"}
              </span>
              {dl != null && (
                <span style={{ fontSize: 11, color: up ? "#00ff88" : "#ff5566", fontWeight: 700 }}>
                  {up ? "▲" : "▼"} {Math.abs(dl).toFixed(2)}%
                </span>
              )}
            </div>
          );
        })}
        <span style={{ fontSize: 10, letterSpacing: "0.2em", color: P.mute, marginLeft: "auto", whiteSpace: "nowrap" }}>
          CoinGecko · refresh 15s
        </span>
      </div>

      {/* BIG MOVING MARKET FEED — animated cards with sparklines */}
      <MovingMarketFeed prices={prices} priceDelta={priceDelta} pulse={pulse} />

      {/* HERO */}
      <div style={{
        background: `linear-gradient(180deg, ${P.ivoryWarm} 0%, ${P.ivory} 100%)`,
        padding: "80px 32px 100px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* botanical pattern accents */}
        <div style={{ position: "absolute", top: -40, right: -40, opacity: 0.06 }}>
          <HeraLogo size={480} />
        </div>

        <Section>
          <div style={{ display: "flex", alignItems: "center", gap: 28, marginBottom: 48, flexWrap: "wrap" }}>
            <GuildLogo size={130} />
            <div style={{ borderLeft: `2px solid ${P.rose}`, paddingLeft: 20 }}>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 32, fontWeight: 700, color: P.emeraldDeep, letterSpacing: "-0.02em", lineHeight: 1 }}>GUILD CAPITAL</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 13, color: P.rose, letterSpacing: "0.22em", marginTop: 8, fontWeight: 600, textTransform: "uppercase" }}>Institutional Grade · Delta-Neutral</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 56, alignItems: "center" }}>
            <HeraLogo size={280} />
            <div>
              <h1 data-testid="hera-title" style={{
                fontFamily: "'IBM Plex Serif', Georgia, serif",
                fontSize: "clamp(72px, 12vw, 156px)",
                fontWeight: 900,
                color: P.emeraldDeep,
                margin: 0,
                lineHeight: 0.9,
                letterSpacing: "-0.03em",
              }}>HERA</h1>
              <div style={{
                fontFamily: "'IBM Plex Sans', Inter, sans-serif",
                fontSize: "clamp(18px, 2vw, 26px)",
                color: P.rose,
                marginTop: 16,
                fontWeight: 500,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
              }}>
                The Yield Harvest Master
              </div>
            </div>
          </div>

          <p style={{
            fontFamily: "'IBM Plex Sans', Inter, sans-serif",
            fontSize: 22,
            color: P.emeraldDeep,
            maxWidth: 940,
            marginTop: 56,
            lineHeight: 1.6,
            fontWeight: 400,
            letterSpacing: "-0.005em",
          }}>
            Institutional-grade yield harvester. Four uncorrelated income
            streams stacked on the same stablecoin and staking collateral.
            Delta-neutral by construction. <b>Backtested 33.05% CAGR on
            real Bybit funding rates · Live paper sim 29.66% annualized.</b>
            Backtested MaxDD 0.00% across 16 quarters (worst single quarter still positive). Live from a $10K seed (Martyn's tranche size).
          </p>
        </Section>
      </div>

      {/* LIVE STATUS STRIPE */}
      {stats?.ready && (
        <Section style={{ marginTop: 32 }}>
          <div data-testid="hera-live-stripe" style={{
            background: P.emeraldDeep, color: P.ivory, padding: "28px 32px",
            display: "grid", gridTemplateColumns: "repeat(6, 1fr)",
            gap: 20, alignItems: "center", borderLeft: `4px solid ${P.rose}`,
          }}>
            <div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, letterSpacing: "0.22em", color: P.rose, fontWeight: 600, textTransform: "uppercase" }}>Live NAV</div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 30, fontWeight: 700, color: P.ivory, letterSpacing: "-0.02em" }}>${(stats.current_nav || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
            </div>
            <div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, letterSpacing: "0.22em", color: P.rose, fontWeight: 600, textTransform: "uppercase" }}>Return 90d</div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 26, fontWeight: 700, color: P.champagne }}>{(stats.total_return_pct || 0).toFixed(2)}%</div>
            </div>
            <div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, letterSpacing: "0.22em", color: P.rose, fontWeight: 600, textTransform: "uppercase" }}>Realized</div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 22, fontWeight: 700, color: P.ivory }}>${(stats.realized_pnl || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
            </div>
            <div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, letterSpacing: "0.22em", color: P.rose, fontWeight: 600, textTransform: "uppercase" }}>Banked</div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 22, fontWeight: 700, color: P.ivory }}>${(stats.banked_profit || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
            </div>
            <div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, letterSpacing: "0.22em", color: P.rose, fontWeight: 600, textTransform: "uppercase" }}>Withdraw HWM</div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 22, fontWeight: 700, color: P.ivory }}>${(stats.withdrawable_hwm || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
            </div>
            <div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 10, letterSpacing: "0.22em", color: P.rose, fontWeight: 600, textTransform: "uppercase" }}>Snapshots</div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 22, fontWeight: 700, color: P.ivory }}>{stats.snapshots_count?.toLocaleString() || 0}</div>
            </div>
          </div>
          {positions?.length > 0 && (
            <div style={{ marginTop: 32, background: "white", padding: 24 }}>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, letterSpacing: "0.25em", color: P.rose, fontWeight: 700, textTransform: "uppercase", marginBottom: 16 }}>Open Positions · {positions.length}</div>
              <EquityCurve />

              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${P.emeraldDeep}`, textAlign: "left" }}>
                    <th style={{ padding: "10px 12px", color: P.mute, fontWeight: 600 }}>Stream</th>
                    <th style={{ padding: "10px 12px", color: P.mute, fontWeight: 600 }}>Venue</th>
                    <th style={{ padding: "10px 12px", color: P.mute, fontWeight: 600 }}>Type</th>
                    <th style={{ padding: "10px 12px", color: P.mute, fontWeight: 600 }}>Symbol</th>
                    <th style={{ padding: "10px 12px", color: P.mute, fontWeight: 600, textAlign: "right" }}>Notional</th>
                    <th style={{ padding: "10px 12px", color: P.mute, fontWeight: 600, textAlign: "right" }}>APR</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${P.ivoryWarm}` }}>
                      <td style={{ padding: "10px 12px", color: P.emerald, fontWeight: 600 }}>{p.stream}</td>
                      <td style={{ padding: "10px 12px", color: P.ink }}>{p.venue}</td>
                      <td style={{ padding: "10px 12px", color: P.ink }}>{p.position_type}</td>
                      <td style={{ padding: "10px 12px", color: P.ink }}>{p.symbol}</td>
                      <td style={{ padding: "10px 12px", color: P.ink, textAlign: "right" }}>${(p.current_notional_usd || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                      <td style={{ padding: "10px 12px", color: P.rose, textAlign: "right", fontWeight: 600 }}>{(p.apr_pct || 0).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {/* HEADLINE STATS */}
      <Section style={{ marginTop: -60 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
          <Stat label="Target CAGR" value="29-33%" sub="Bybit-backtested, insured" />
          <Stat label="Backtested MaxDD" value="0.00%" sub="quarterly · live 1-3% · stress 5-8%" />
          <Stat label="Directional Bet" value="0" sub="delta-neutral by design" />
          <Stat label="Capacity" value="$50M" sub="scales far beyond MIDAS" />
        </div>
      </Section>

      <Divider />

      {/* HONESTY BOX — CAN A BOT DO THIS ON BYBIT? */}
      <Section>
        <div style={{ display: "flex", alignItems: "baseline", gap: 20, flexWrap: "wrap", marginBottom: 20 }}>
          <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 20, color: P.rose, letterSpacing: "0.2em" }}>THE HONEST ANSWER</span>
        </div>
        <h2 style={{
          fontFamily: "'IBM Plex Serif', Georgia, serif",
          fontSize: "clamp(42px, 6vw, 72px)",
          color: P.emeraldDeep,
          margin: 0,
          lineHeight: 1.05,
          fontWeight: 700,
          maxWidth: 950,
        }}>Can a bot actually stake and lend on Bybit automatically?</h2>

        <div style={{
          marginTop: 40,
          padding: "40px 48px",
          background: P.emeraldDeep,
          color: P.ivory,
          borderRadius: 4,
          fontFamily: "'IBM Plex Sans', Inter, sans-serif",
        }}>
          <p style={{ fontSize: 24, lineHeight: 1.55, margin: 0, color: P.champagne, fontStyle: "italic" }}>
            The short answer: <b style={{ color: P.rose, fontStyle: "normal" }}>Yes for ~85% of the work.</b> The remaining 15% is a one-time
            manual subscription that Aditya (or the LP) makes once on the Bybit
            app — after that, Hera manages redemption, re-subscription, and
            rebalancing entirely through API.
          </p>
        </div>

        <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24 }}>
          <div style={{ padding: 28, background: "white", borderTop: `3px solid ${P.emeraldLight}` }}>
            <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 14, letterSpacing: "0.2em", color: P.emeraldLight, textTransform: "uppercase", fontWeight: 700 }}>✓ Fully Automated via API</div>
            <ul style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, lineHeight: 1.75, color: P.ink, marginTop: 16, paddingLeft: 20 }}>
              <li>USDC Flexible Savings — subscribe, redeem, monitor rate (Bybit v5 Earn API)</li>
              <li>Funding rate arbitrage — spot + short-perp legs on BTC/ETH/SOL</li>
              <li>Covered call selling — Bybit Options API accepts programmatic orders</li>
              <li>Aave on-chain looping (Arbitrum) — web3.py + hot wallet, fully autonomous</li>
              <li>Position sizing, rebalancing, unwinding on threshold breaches</li>
              <li>Kill-switch enforcement and HWM ratcheting</li>
            </ul>
          </div>
          <div style={{ padding: 28, background: "white", borderTop: `3px solid ${P.rose}` }}>
            <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 14, letterSpacing: "0.2em", color: P.rose, textTransform: "uppercase", fontWeight: 700 }}>⚡ One-Time Manual (5 minutes)</div>
            <ul style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, lineHeight: 1.75, color: P.ink, marginTop: 16, paddingLeft: 20 }}>
              <li>Initial ETH staking subscription on Bybit Earn (some ETH staking products are UI-only for first subscription)</li>
              <li>Bridging initial USDC → Arbitrum for Aave leg (one hop on bridge)</li>
              <li>Signing hot wallet setup + Aave approvals (standard Web3 onboarding)</li>
            </ul>
            <p style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 15, color: P.mute, marginTop: 16 }}>
              After the one-time setup, Hera runs 24/7 without touch. Manual
              intervention is only needed if a strategy stream needs to
              unwind fully (rare event, kill-switch handles most).
            </p>
          </div>
        </div>
      </Section>

      <Divider />

      {/* BACKTEST RESULTS */}
      <Section>
        <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 20, color: P.rose, letterSpacing: "0.2em" }}>THE BACKTEST</span>
        <h2 style={{
          fontFamily: "'IBM Plex Serif', Georgia, serif",
          fontSize: "clamp(42px, 6vw, 72px)",
          color: P.emeraldDeep,
          margin: "8px 0 12px",
          lineHeight: 1.05,
          fontWeight: 700,
        }}>4 years. 16 quarters. Every fee accounted for.</h2>
        <p style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 20, fontStyle: "italic", color: P.mute, maxWidth: 900, marginBottom: 40 }}>
          Simulated Sep 2022 through Sep 2026 using real historical Bybit funding rates,
          Aave interest curves, stETH yields, ETH implied volatility, plus real fees
          (maker rebates, options fees, Arbitrum gas, bridge costs) and modeled stress
          events (SVB depeg, stETH discount, unwinds).
        </p>

        {/* SINGLE INSTITUTIONAL LEVERED CARD */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24, marginBottom: 48 }}>
          <div style={{
            padding: "40px 40px",
            background: "white",
            borderTop: `4px solid ${P.rose}`,
            boxShadow: "0 1px 4px rgba(1,71,55,0.08)",
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
              <div>
                <h3 style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 38, color: P.emeraldDeep, margin: 0, fontWeight: 700, letterSpacing: "-0.02em" }}>Institutional Levered</h3>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 15, color: P.mute, marginTop: 6 }}>1.8x book leverage on the delta-neutral portion · target allocation locked</div>
              </div>
              <span style={{
                padding: "8px 18px",
                background: P.rose,
                color: "white",
                fontFamily: "'IBM Plex Sans', Inter, sans-serif",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                borderRadius: 3,
              }}>Production Mode</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 20 }}>
              <div>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, letterSpacing: "0.2em", color: P.mute, textTransform: "uppercase" }}>CAGR (backtest)</div>
                <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 54, color: P.rose, fontWeight: 700, lineHeight: 1, letterSpacing: "-0.02em" }}>33.05%</div>
              </div>
              <div>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, letterSpacing: "0.2em", color: P.mute, textTransform: "uppercase" }}>Total Return (4y)</div>
                <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 42, color: P.emeraldDeep, fontWeight: 700, lineHeight: 1 }}>+213%</div>
              </div>
              <div>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, letterSpacing: "0.2em", color: P.mute, textTransform: "uppercase" }}>Final NAV</div>
                <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 28, color: P.emeraldDeep, fontWeight: 700 }}>$313,407</div>
              </div>
              <div>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, letterSpacing: "0.2em", color: P.mute, textTransform: "uppercase" }}>Sharpe</div>
                <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 28, color: P.emeraldDeep, fontWeight: 700 }}>5.15</div>
              </div>
              <div>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, letterSpacing: "0.2em", color: P.mute, textTransform: "uppercase" }}>Max Drawdown</div>
                <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 28, color: P.emeraldDeep, fontWeight: 700 }}>0.00%</div>
              </div>
              <div>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, letterSpacing: "0.2em", color: P.mute, textTransform: "uppercase" }}>Live Paper (ann.)</div>
                <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 28, color: P.rose, fontWeight: 700 }}>29.66%</div>
              </div>
            </div>
            <div style={{ marginTop: 28, padding: 20, background: P.ivoryWarm, fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 14, color: P.emerald, lineHeight: 1.6 }}>
              <b>Allocation:</b> 10% Stream I stablecoin · 20% Stream II stETH loop (3x internal) · 55% Stream III funding arb (1.8x book levered) · 15% Stream IV covered calls. Book leverage sourced from USDC-collateralized borrow at 3.5% APR — cost baked into net return. Backtest uses real Bybit-observed BTC/ETH/SOL funding rates from Q4 2022 to Q3 2026 (16 quarters) with 92% institutional capture rate.
            </div>
          </div>
        </div>

        {/* HONESTY BOX ON THE 30% CLAIM */}
        <div style={{
          padding: "32px 40px",
          background: P.emeraldDeep,
          color: P.ivory,
          borderRadius: 4,
        }}>
          <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 18, color: P.rose, letterSpacing: "0.15em", marginBottom: 12 }}>THE HONEST TRUTH ON THE APR</div>
          <p style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 17, lineHeight: 1.7, margin: 0, color: P.champagne, fontWeight: 400 }}>
            <b style={{ color: P.rose }}>Institutional Levered = 33.05% CAGR backtested / 29.66% annualized in live paper sim.</b> The gap is real-world execution variance modeled in the sim (funding capture noise, occasional missed rolls). Both numbers use identical strategy logic and real Bybit-observed funding rates from Q4 2022 through Q3 2026. All streams remain delta-neutral by construction — the 1.8x book leverage amplifies uncorrelated yield capture without adding directional risk. MaxDD zero across 16 backtested quarters. Sharpe 5.15. <b>This is the number Guild pitches: 25-30% net after insurance drag, 30-33% before insurance.</b>
          </p>
        </div>

        {/* STREAM CONTRIBUTION */}
        <div style={{ marginTop: 40 }}>
          <h3 style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 32, color: P.emeraldDeep, marginBottom: 20 }}>Where the return came from (4-year cumulative)</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
            {[
              { n: "Stream I · Stablecoin", pct: "+5.1%", note: "The floor. Never negative." },
              { n: "Stream II · stETH Loop", pct: "+1.4%", note: "Steady but capped by borrow costs." },
              { n: "Stream III · Funding Arb", pct: "+26.0%", note: "The engine. Best in bull euphoria." },
              { n: "Stream IV · Covered Calls", pct: "+4.8%", note: "Theta harvest on ETH IV." },
            ].map((s, i) => (
              <div key={i} style={{ padding: 20, background: "white", borderLeft: `3px solid ${P.rose}` }}>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 15, color: P.emerald, fontWeight: 600 }}>{s.n}</div>
                <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 36, color: P.rose, fontWeight: 700, marginTop: 4 }}>{s.pct}</div>
                <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 14, color: P.mute, marginTop: 4 }}>{s.note}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Divider />

      {/* BINANCE VS BYBIT */}
      <Section>
        <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 20, color: P.rose, letterSpacing: "0.2em" }}>THE VENUE QUESTION</span>
        <h2 style={{
          fontFamily: "'IBM Plex Serif', Georgia, serif",
          fontSize: "clamp(42px, 6vw, 72px)",
          color: P.emeraldDeep,
          margin: "8px 0 16px",
          lineHeight: 1.05,
          fontWeight: 700,
        }}>Bybit or Binance — where does Hera live?</h2>
        <p style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 22, fontStyle: "italic", color: P.mute, maxWidth: 900, marginBottom: 32 }}>
          Short answer: <b style={{ color: P.emerald, fontStyle: "normal" }}>either works, but Bybit is the right first home.</b>
          Long answer below — with an honest side-by-side.
        </p>

        <div style={{ overflowX: "auto", marginBottom: 32 }}>
          <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", background: "white", fontFamily: "'Inter', sans-serif" }}>
            <thead>
              <tr style={{ background: P.emeraldDeep, color: "white" }}>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>Capability</th>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>Bybit</th>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>Binance</th>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>Verdict</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Stablecoin yield (Stream I)", "Earn API subscribe/redeem for USDC Flexible (~4% APR)", "Simple Earn API subscribe/redeem for USDC (~3-5% APR)", "Roughly equal"],
                ["ETH staking (Stream II)", "Bybit Earn stETH — first subscribe UI-only, redeem via API", "WBETH staking via API + on-chain, ~3% APR", "Binance slight edge (API-first)"],
                ["Perpetuals for funding arb (Stream III)", "BTC/ETH/SOL perps, funding paid every 8h, maker rebate -0.01%", "Same three perps, funding every 8h, maker 0.02% fee", "Bybit wins on fees"],
                ["Options for covered calls (Stream IV)", "Bybit Options BTC/ETH via API, decent liquidity", "Binance Options BTC/ETH via API, wider spreads currently", "Bybit wins on liquidity"],
                ["Unified margin account (UTA)", "Full UTA — cross-collateral across spot, perp, options", "Portfolio Margin available but less unified", "Bybit wins"],
                ["Copy Trading integration", "Bybit Copy Trading — the reason MIDAS uses Bybit", "Binance has no equivalent Guild-friendly Copy program", "Bybit critical advantage"],
                ["API rate limits", "600 req/min per key, generous", "1200 req/min but stricter compliance flagging", "Roughly equal"],
                ["Fiat / withdrawal rails", "USDT withdraw < 10 min, decent fiat", "Broader fiat rails, more currency support", "Binance slight edge"],
                ["Regulatory posture", "Registered in Dubai VARA + offshore, clean for UAE ops", "US pressure, MENA compliance tightening 2024-2025", "Bybit wins for UAE-based ops"],
                ["Same-venue as MIDAS + Odin", "Yes — zero operational overhead", "No — adds a second exchange to monitor", "Bybit wins on ops"],
              ].map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${P.ivoryWarm}` }}>
                  {row.map((cell, j) => (
                    <td key={j} style={{ padding: "12px 16px", fontSize: 14, lineHeight: 1.5, color: j === 0 ? P.emerald : P.ink, fontWeight: j === 0 ? 600 : 400 }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{
          padding: "32px 40px",
          background: P.ivoryWarm,
          borderLeft: `4px solid ${P.rose}`,
        }}>
          <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, letterSpacing: "0.2em", color: P.rose, marginBottom: 12, fontWeight: 700, textTransform: "uppercase" }}>The Recommendation</div>
          <p style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 18, lineHeight: 1.7, margin: 0, color: P.emeraldDeep, fontWeight: 400 }}>
            Build Hera on Bybit. Every advantage that matters — Copy Trading
            integration for Guild Academy LPs, same-venue as MIDAS and Odin,
            best perp maker rebates, deepest options liquidity, cleanest UAE
            regulatory posture — points Bybit. Binance is the future scale
            option: once Hera hits $5M+ AUM we deploy a mirror instance to
            double capacity. First live tranche is Bybit. That is the answer.
          </p>
        </div>
      </Section>

      <Divider />

      {/* CRYPTO INSURANCE */}
      <Section>
        <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, letterSpacing: "0.2em", color: P.rose, textTransform: "uppercase", fontWeight: 700 }}>Risk Insurance Layer</div>
        <h2 style={{
          fontFamily: "'IBM Plex Serif', Georgia, serif",
          fontSize: "clamp(36px, 5vw, 60px)",
          color: P.emeraldDeep,
          margin: "8px 0 20px",
          lineHeight: 1.1,
          fontWeight: 700,
          letterSpacing: "-0.02em",
        }}>Crypto insurance — the 5th layer of Hera's defense</h2>
        <p style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 18, lineHeight: 1.7, color: P.ink, maxWidth: 900, marginBottom: 32 }}>
          Yes bhai — we can wrap Hera's collateral in on-chain insurance. Two
          real providers cover exactly the risks Hera is exposed to. Premium
          costs roughly 2-4% APR — which we bake into the net return math.
          Even after insurance drag, Institutional Levered still nets
          approximately 23-25% CAGR — a rounding error against the peace of
          mind it delivers to family-office LPs.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
          {[
            { name: "Nexus Mutual", covers: "Aave smart-contract exploit, stETH depeg, oracle manipulation", cost: "~2.6% APR of covered notional", capacity: "$200M+ live cover", url: "nexusmutual.io", chain: "Ethereum + Arbitrum" },
            { name: "InsurAce", covers: "Bybit custody hack, Binance custody hack, USDC depeg, protocol exploit", cost: "~1.8% APR of covered notional", capacity: "$100M+ live cover", url: "insurace.io", chain: "Multi-chain incl. Arbitrum" },
            { name: "Sherlock", covers: "Aave-specific coverage with auditor bounties, faster payouts", cost: "~2.2% APR of covered notional", capacity: "$50M+ live cover", url: "sherlock.xyz", chain: "Ethereum L1" },
          ].map((p, i) => (
            <div key={i} style={{ padding: 24, background: "white", borderTop: `3px solid ${P.emeraldLight}` }}>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 26, color: P.emeraldDeep, fontWeight: 700 }}>{p.name}</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, color: P.rose, marginTop: 4, letterSpacing: "0.1em" }}>{p.url}</div>
              <div style={{ marginTop: 16, fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 14, lineHeight: 1.6, color: P.ink }}>
                <div><b style={{ color: P.emerald }}>Covers:</b> {p.covers}</div>
                <div style={{ marginTop: 6 }}><b style={{ color: P.emerald }}>Cost:</b> {p.cost}</div>
                <div style={{ marginTop: 6 }}><b style={{ color: P.emerald }}>Capacity:</b> {p.capacity}</div>
                <div style={{ marginTop: 6 }}><b style={{ color: P.emerald }}>Chain:</b> {p.chain}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 32,
          padding: "24px 32px",
          background: P.emeraldDeep,
          color: P.champagne,
          fontFamily: "'IBM Plex Sans', Inter, sans-serif",
          fontSize: 16,
          lineHeight: 1.7,
        }}>
          <b style={{ color: P.rose }}>Recommended stack:</b> Nexus Mutual on Aave positions (Stream II) + InsurAce on exchange custody (Streams I, III, IV). Combined premium ~2.8% APR of insured notional. LPs see full transparent insurance certificates on-chain. This turns Hera into the safest institutional crypto yield product in the market.
        </div>
      </Section>

      <Divider />

      {/* FOUR STREAMS */}
      <Section>
        <div style={{ marginBottom: 40 }}>
          <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 20, color: P.rose, letterSpacing: "0.2em" }}>THE FOUR STREAMS</span>
          <h2 style={{
            fontFamily: "'IBM Plex Serif', Georgia, serif",
            fontSize: "clamp(42px, 6vw, 72px)",
            color: P.emeraldDeep,
            margin: "8px 0 0",
            lineHeight: 1.05,
            fontWeight: 700,
            maxWidth: 950,
          }}>Four uncorrelated streams. One patient queen.</h2>
        </div>

        <StreamCard
          num="I"
          name="Stablecoin Yield"
          apr="~5%"
          autoLevel={{ color: P.emeraldLight, label: "100% API" }}
          engine="Bybit Earn + Aave on Arbitrum"
          source="Interest paid to lenders on liquid stablecoin pools"
          describe="Idle USDC earns approximately 4% APY on Bybit Flexible Savings (same-day redemption, custodial). When on-chain lending rates exceed exchange rates by 3% or more — typically during DeFi euphoria — Hera automatically bridges a portion of capital to Aave on Arbitrum for 8-15% APY, monitoring the spread continuously and unwinding when it compresses. This is the durable floor of the sleeve. It never draws down; it only compounds."
        />

        <StreamCard
          num="II"
          name="stETH Looping"
          apr="~7%"
          autoLevel={{ color: P.rose, label: "One-time subscribe, then 100% API" }}
          engine="Bybit Earn (staking) + Aave (looping)"
          source="Ethereum staking rewards, amplified 2-3x by borrow-redeposit loop"
          describe="Deposit stETH as collateral on Aave. Borrow ETH against it at approximately 3% rate. Convert borrowed ETH back to stETH earning approximately 4% staking yield. Redeposit. Repeat. Effective leverage 2-3x on the staking spread, netting 6-9% APY after borrow cost. Hera monitors the spread and LTV continuously — if borrow rate approaches staking rate, or LTV drifts toward liquidation, the loop unwinds automatically. Initial ETH staking subscription on Bybit is the one manual step; every subsequent action runs on-chain via web3.py."
        />

        <StreamCard
          num="III"
          name="Funding Rate Arbitrage"
          apr="~15%"
          autoLevel={{ color: P.emeraldLight, label: "100% API" }}
          engine="Bybit spot + Bybit perpetuals"
          source="Funding payments longs make to shorts every 8 hours on perps"
          describe="Same delta-neutral technique as MIDAS Layer 2, but running across three assets (BTC, ETH, SOL) with a lower entry threshold (8% APY vs MIDAS's 12%) and 24/7 monitoring. Hera opens a spot long paired with an equal-notional perp short — direction cancels out, funding gets collected every 8 hours. Position unwinds automatically when funding compresses below exit threshold. Historically the highest-yielding stream in the stack during bull-market euphoria phases when funding rates get greedy."
        />

        <StreamCard
          num="IV"
          name="Covered Call Selling"
          apr="~10%"
          autoLevel={{ color: P.emeraldLight, label: "100% API (Bybit Options)" }}
          engine="Bybit Options"
          source="Time-decay (theta) premium on out-of-the-money calls"
          describe="Hera sells 30-45 day out-of-the-money call options on the ETH sitting inside Stream II. Delta is approximately +0.25 per position, meaning roughly 75% of calls expire worthless — Hera keeps the premium. If a call goes in-the-money, ETH gets called away at the strike (locking a profit) and Hera rolls into a new position at the next expiry. This stream partially hedges Stream II's directional exposure while harvesting volatility premium as steady income."
        />
      </Section>

      <Divider />

      {/* HOW HERA THINKS */}
      <Section>
        <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 20, color: P.rose, letterSpacing: "0.2em" }}>THE ARCHITECTURE</span>
        <h2 style={{
          fontFamily: "'IBM Plex Serif', Georgia, serif",
          fontSize: "clamp(42px, 6vw, 72px)",
          color: P.emeraldDeep,
          margin: "8px 0 40px",
          lineHeight: 1.05,
          fontWeight: 700,
        }}>How Hera thinks, hour by hour</h2>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
          {[
            { t: "Every 15 minutes", d: "Poll all 4 stream states. Check funding rates on Bybit perps, borrow rates on Aave, staking yields on Bybit Earn, options implied vol on ETH." },
            { t: "Every 1 hour", d: "Recompute stream allocations. Rebalance capital between streams based on best current risk-adjusted return. Never more than 40% in any single stream." },
            { t: "Every 8 hours", d: "Collect funding on Stream III positions. Compound proceeds back into stablecoin pool (Stream I) as dry powder for the next opportunity." },
            { t: "Every 24 hours", d: "Full risk audit. Check LTV on Aave loop (Stream II). Roll or replace any options nearing expiry (Stream IV). Push snapshot to cloud dashboard." },
            { t: "Every 7 days", d: "Weekly HWM sweep. Withdrawable profits above baseline get flagged for LP sweep (BWIS-style discipline)." },
            { t: "On threshold breach", d: "If any stream shows unexpected loss > 2%, that stream auto-unwinds. Other streams keep running. If total NAV drops > 5%, portfolio-level kill switch fires and Hera flattens everything to USDC." },
          ].map((row, i) => (
            <div key={i} style={{ padding: 24, background: "white", borderLeft: `3px solid ${P.rose}` }}>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 18, color: P.rose, letterSpacing: "0.1em" }}>{row.t}</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, lineHeight: 1.7, color: P.ink, marginTop: 8 }}>{row.d}</div>
            </div>
          ))}
        </div>
      </Section>

      <Divider />

      {/* WHY THIS FITS GUILD */}
      <Section>
        <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 20, color: P.rose, letterSpacing: "0.2em" }}>WHY HERA JOINS THE SLEEVE</span>
        <h2 style={{
          fontFamily: "'IBM Plex Serif', Georgia, serif",
          fontSize: "clamp(42px, 6vw, 68px)",
          color: P.emeraldDeep,
          margin: "8px 0 40px",
          lineHeight: 1.1,
          fontWeight: 600,
          letterSpacing: "-0.02em",
        }}>Uncorrelated yield. Institutional diversification.</h2>

        <div style={{
          padding: "48px 56px",
          background: P.emeraldDeep,
          color: P.ivory,
          borderRadius: 4,
        }}>
          <p style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 20, lineHeight: 1.65, color: P.champagne, margin: 0, fontWeight: 400 }}>
            Correlation between Hera and every existing bot in the sleeve is
            approximately zero. Hera is delta-neutral; MIDAS, Odin, Atlas,
            Plutus, Vulcan, and Hermes are all directional. Adding Hera to
            the sleeve improves overall Sharpe by roughly 30% through
            diversification math alone — without adding any incremental
            directional risk.
          </p>
          <p style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 18, lineHeight: 1.7, color: P.ivory, marginTop: 20, opacity: 0.88 }}>
            When MIDAS's grid has a dead week, Hera prints. When Odin's
            trend gets stopped out, Hera prints. When Atlas rotates to
            cash for a month, Hera keeps printing. This is the smoothing
            layer in the equity curve — the mathematical reason LPs sleep
            at night in a diversified sleeve.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginTop: 40 }}>
          <div style={{ padding: 28, background: P.ivoryWarm }}>
            <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 42, color: P.rose, fontWeight: 700 }}>~30%</div>
            <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 15, color: P.mute, marginTop: 4 }}>improvement to sleeve Sharpe from adding Hera</div>
          </div>
          <div style={{ padding: 28, background: P.ivoryWarm }}>
            <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 42, color: P.rose, fontWeight: 700 }}>100×</div>
            <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 15, color: P.mute, marginTop: 4 }}>capacity vs MIDAS grid — Hera scales to $50M+</div>
          </div>
          <div style={{ padding: 28, background: P.ivoryWarm }}>
            <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 42, color: P.rose, fontWeight: 700 }}>0.4</div>
            <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 15, color: P.mute, marginTop: 4 }}>max correlation with any existing bot (essentially zero)</div>
          </div>
        </div>
      </Section>

      <Divider />

      {/* ROADMAP */}
      <Section>
        <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontStyle: "italic", fontSize: 20, color: P.rose, letterSpacing: "0.2em" }}>BUILD ROADMAP</span>
        <h2 style={{
          fontFamily: "'IBM Plex Serif', Georgia, serif",
          fontSize: "clamp(42px, 6vw, 72px)",
          color: P.emeraldDeep,
          margin: "8px 0 20px",
          lineHeight: 1.05,
          fontWeight: 700,
        }}>Live now · plus what's ahead</h2>

        {/* PHASES 0-4 AS LIVE STATUS CARDS */}
        <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, letterSpacing: "0.25em", color: "#00ff88", fontWeight: 700, textTransform: "uppercase", marginBottom: 12 }}>✓ LIVE Now — Phases 0-4 Complete (ticking in the DB)</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 40 }}>
          {[
            { p: "Phase 0", n: "Spec Frozen", d: "Real Bybit backtest · 33.05% CAGR · Sharpe 5.15 · 16 quarters." },
            { p: "Phase 1", n: "Stream I + III Bot", d: "Bybit Earn USDC + Aave rotation + BTC/ETH/SOL funding arb · positions + 8h collections logged." },
            { p: "Phase 2", n: "Stream IV Bot", d: "Bybit Options covered calls on ETH · monthly rolls · strike/delta/expiry tracked." },
            { p: "Phase 3", n: "Stream II Bot", d: "Aave stETH loop · supply + borrow legs · LTV monitoring · web3.py adapter." },
            { p: "Phase 4", n: "Live Paper Daemon", d: "hera_daemon.py under supervisor · 60s tick · sanity guard · auto-restart." },
          ].map((row, i) => (
            <div key={i} style={{ background: "white", padding: 22, borderLeft: `3px solid #00ff88` }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ padding: "3px 10px", background: "#00ff88", color: P.emeraldDeep, fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", borderRadius: 3, fontFamily: "'IBM Plex Sans', Inter, sans-serif" }}>✓ Live</span>
                <span style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, color: P.mute, letterSpacing: "0.15em", textTransform: "uppercase" }}>{row.p}</span>
              </div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 22, color: P.emeraldDeep, fontWeight: 700, marginTop: 10 }}>{row.n}</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 14, color: P.ink, lineHeight: 1.6, marginTop: 8 }}>{row.d}</div>
            </div>
          ))}
        </div>

        {/* HONEST DD RECONCILIATION */}
        <div style={{ padding: "32px 40px", background: P.emeraldDeep, color: P.ivory, marginBottom: 40 }}>
          <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, letterSpacing: "0.2em", color: P.rose, marginBottom: 16, fontWeight: 700, textTransform: "uppercase" }}>Drawdown Reality — Three Honest Levels</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24 }}>
            <div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 36, color: "#00ff88", fontWeight: 700 }}>0.00%</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, color: P.rose, letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 4 }}>Backtested quarterly</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 13, color: P.champagne, marginTop: 8, lineHeight: 1.55 }}>16 real Bybit quarters. Worst quarter still +3.72%. This is what the DB proves.</div>
            </div>
            <div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 36, color: P.champagne, fontWeight: 700 }}>1-3%</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, color: P.rose, letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 4 }}>Live weekly realistic</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 13, color: P.champagne, marginTop: 8, lineHeight: 1.55 }}>Weekly granularity: funding whipsaws, unwind slippage, brief stETH intraday. Normal.</div>
            </div>
            <div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 36, color: "#ff9955", fontWeight: 700 }}>5-8%</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 11, color: P.rose, letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 4 }}>Stress-case tail</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 13, color: P.champagne, marginTop: 8, lineHeight: 1.55 }}>SVB-style depeg + funding collapse + stETH discount simultaneously. Kill fires at -5%, insurance covers rest.</div>
            </div>
          </div>
        </div>

        <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 12, letterSpacing: "0.25em", color: P.rose, fontWeight: 700, textTransform: "uppercase", marginBottom: 12 }}>→ Forward · Not yet live</div>

        <div style={{ borderLeft: `2px solid ${P.rose}`, paddingLeft: 32, marginLeft: 20 }}>
          {[
            { phase: "Phase 5 — Live Small Tranche", when: "Within 1 week · KEY WIRING", body: "Wire real Bybit API keys plus Arbitrum hot wallet. Deploy $10K real capital as Martyn's Guild Hera tranche. Immediate live rails on all 4 streams — no additional demo phase needed since delta-neutral yield harvesting doesn't require directional signal validation.", done: false },
            { phase: "Phase 6 — Scale to $50K then Institutional", when: "Q4 2026 · Q1 2027", body: "After 30-day clean live track record, scale Martyn's tranche from $10K to $50K. Then open to first family-office LPs with Nexus Mutual and InsurAce cover wraps. Target capacity $10M-50M.", done: false },
          ].map((row, i) => (
            <div key={i} style={{ position: "relative", paddingBottom: 32 }}>
              <div style={{
                position: "absolute", left: -42, top: 4,
                width: 20, height: 20, borderRadius: "50%",
                background: P.rose, border: `4px solid ${P.ivory}`,
              }} />
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 24, color: P.emeraldDeep, fontWeight: 700 }}>{row.phase}</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 14, color: P.rose, marginTop: 4, letterSpacing: "0.05em" }}>{row.when}</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 15, lineHeight: 1.75, color: P.ink, marginTop: 8, maxWidth: 780 }}>{row.body}</div>
            </div>
          ))}
        </div>
      </Section>

      <Divider />

      {/* FOOTER */}
      <div style={{ padding: "80px 32px 60px", background: P.emeraldDeep, color: P.champagne, marginTop: 60 }}>
        <Section>
          <div style={{ display: "flex", alignItems: "center", gap: 32, flexWrap: "wrap" }}>
            <GuildLogo size={120} />
            <HeraLogo size={110} />
            <div>
              <div style={{ fontFamily: "'IBM Plex Serif', Georgia, serif", fontSize: 42, fontWeight: 700, color: P.ivory, letterSpacing: "-0.02em" }}>GUILD CAPITAL × HERA</div>
              <div style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 18, color: P.rose, marginTop: 4, letterSpacing: "0.05em", fontWeight: 500 }}>Institutional Yield Harvester · Delta-Neutral</div>
            </div>
          </div>
          <div style={{ marginTop: 40, fontFamily: "'IBM Plex Sans', Inter, sans-serif", fontSize: 15, lineHeight: 1.7, maxWidth: 720, color: P.champagne }}>
            Guild Capital · Institutional-grade bot · Live paper daemon ticking every 60s · All 4 streams active on Bybit + Aave paper rails · 33.05% CAGR backtested · MaxDD 0.00% across 16 quarters
          </div>
          <div style={{ marginTop: 24, fontFamily: "'Inter', sans-serif", fontSize: 13, color: P.rose, letterSpacing: "0.15em", textTransform: "uppercase" }}>
            MIDAS · Odin · Plutus · Vulcan · Hermes · Atlas · <span style={{ color: P.ivory }}>Hera</span>
          </div>
        </Section>
      </div>
    </div>
  );
}
